import React, { useEffect } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import * as Sentry from '@sentry/react'
import { Trans, useTranslation } from 'react-i18next'

// Who to write to about a parcel that is placed but has no controller. The
// subject is for the colleague reading it, not the customer, so it stays as
// it was written.
const CONTACT_EMAIL = 'robert.kofoed@qlocx.com'
const CONTACT_SUBJECT = 'Fel vad är detta för produkt!?'

// This part is about what is wrong with the parcel, and has two unrelated ways
// to be non-empty, which the registry's AND-of-terms criteria cannot say; so the
// registry shows it for every parcel and each block gates itself:
// - the error list, for a parcel that is `created` and has errors in either of
//   its two fields. It starts folded away, as the modal's did.
// - the warning for a parcel that is placed but has no controller, in any status.
export default function Errors({ parcel }) {
  const { t } = useTranslation('parcels')
  const { t: transCommon } = useTranslation('common')

  // The modal replaced its whole body with this. The page keeps the other
  // sections beside it.
  const noController =
    Boolean(parcel.location) &&
    parcel.isAllocated !== false &&
    !parcel.controller

  // The alert reaches whoever happens to read it; this reaches iBoxen. Once per
  // parcel: the effect is keyed on the ids, not on the parcel object a reload
  // replaces. Like every hook in a section body it runs before the early return.
  const { _id, parcelId } = parcel
  useEffect(() => {
    if (!noController) return
    Sentry.captureException(
      new Error(
        `Parcel ${parcelId} (${_id}) is placed at a location but has no controller`
      )
    )
  }, [noController, _id, parcelId])

  // an empty list is no error
  const showErrors =
    parcel.currentStatus === 'created' &&
    Boolean(parcel.errors?.length || parcel.parcelErrors?.length)
  if (!showErrors && !noController) return null

  const describe = (error) =>
    `${window.niceDate(error.created)} - ${t(`errors.${error.name}`)}${
      error.status ? ` - Status ${error.status}` : ''
    }`

  const errors = [...(parcel.errors || []), ...(parcel.parcelErrors || [])]

  return (
    <Stack spacing={1.5}>
      {noController && (
        <Alert severity="warning">
          <Trans
            t={t}
            i18nKey="noControllerContact"
            values={{ email: CONTACT_EMAIL }}
            components={{
              mail: (
                <Link
                  href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                    CONTACT_SUBJECT
                  )}`}
                />
              ),
            }}
          />
        </Alert>
      )}
      {showErrors && (
        <Accordion
          disableGutters
          elevation={0}
          TransitionProps={{ unmountOnExit: true }}
          // the page's card is the frame; the accordion's own shadow and divider line would double it
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            {/* not a heading: the box around this is already titled with the
                same word in all three languages, and the accordion button is
                the affordance. This is its label, not a second heading. */}
            <Typography variant="h6" component="span">
              ⚠️ {transCommon('error')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.5}>
              {errors.map((error, i) => (
                <Typography
                  // the same error can be on both lists, so the content alone is not a key
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${JSON.stringify(error)}-${i}`}
                  variant="body2"
                >
                  {describe(error)}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  )
}
